import { useCallback, useEffect, useRef, useState } from 'react';

import { getApiBaseUrl } from '@/core/lib/file-url';
import { useAuthStore } from '@/core/store/auth.store';
import {
  parseRealtimeEventPayload,
  type RealtimeEvent,
} from '@/core/types/realtime-events';

export type WorkspaceRealtimeConnectionState =
  | 'idle'
  | 'connecting'
  | 'open'
  | 'closed'
  | 'error';

const MAX_BACKOFF_MS = 30_000;
const INITIAL_BACKOFF_MS = 1_000;

function waitForReconnect(
  backoffMs: number,
  signal: AbortSignal,
): Promise<void> {
  return new Promise((resolve) => {
    if (signal.aborted) {
      resolve();
      return;
    }

    let timer: ReturnType<typeof setTimeout> | null = null;

    const finish = () => {
      if (timer) clearTimeout(timer);
      document.removeEventListener('visibilitychange', onVisible);
      resolve();
    };

    const onVisible = () => {
      if (document.visibilityState === 'visible') {
        finish();
      }
    };

    timer = setTimeout(finish, backoffMs);

    if (
      typeof document !== 'undefined' &&
      document.visibilityState === 'hidden'
    ) {
      if (timer) clearTimeout(timer);
      document.addEventListener('visibilitychange', onVisible);
    }
  });
}

function parseSseBuffer(buffer: string): {
  events: RealtimeEvent[];
  remainder: string;
} {
  const events: RealtimeEvent[] = [];
  const parts = buffer.split('\n\n');
  const remainder = parts.pop() ?? '';

  for (const part of parts) {
    const lines = part.split('\n');
    for (const line of lines) {
      if (!line.startsWith('data:')) continue;
      const payload = line.slice(5).trim();
      if (!payload) continue;
      const event = parseRealtimeEventPayload(payload);
      if (event) events.push(event);
    }
  }

  return { events, remainder };
}

export type UseWorkspaceRealtimeOptions = {
  workspaceId: string | undefined;
  enabled: boolean;
  onEvent: (event: RealtimeEvent) => void;
};

export function useWorkspaceRealtime({
  workspaceId,
  enabled,
  onEvent,
}: UseWorkspaceRealtimeOptions) {
  const [connectionState, setConnectionState] =
    useState<WorkspaceRealtimeConnectionState>('idle');
  const onEventRef = useRef(onEvent);
  useEffect(() => {
    onEventRef.current = onEvent;
  }, [onEvent]);

  const connect = useCallback(
    async (signal: AbortSignal) => {
      if (!workspaceId) return;

      const accessToken = useAuthStore.getState().tokens?.accessToken;
      if (!accessToken) {
        setConnectionState('error');
        return;
      }

      const baseUrl = getApiBaseUrl().replace(/\/$/, '');
      const url = `${baseUrl}/api/v1/workspaces/${workspaceId}/realtime/stream`;

      setConnectionState('connecting');

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          Accept: 'text/event-stream',
        },
        signal,
      });

      if (!response.ok || !response.body) {
        throw new Error(`Realtime stream failed: ${response.status}`);
      }

      setConnectionState('open');

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (!signal.aborted) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const parsed = parseSseBuffer(buffer);
        buffer = parsed.remainder;
        for (const event of parsed.events) {
          onEventRef.current(event);
        }
      }
    },
    [workspaceId],
  );

  useEffect(() => {
    if (!enabled || !workspaceId) {
      return;
    }

    const abortController = new AbortController();
    let backoffMs = INITIAL_BACKOFF_MS;
    let disposed = false;

    const run = async () => {
      while (!disposed && !abortController.signal.aborted) {
        try {
          await connect(abortController.signal);
          if (!disposed) {
            setConnectionState('closed');
          }
          backoffMs = INITIAL_BACKOFF_MS;
        } catch {
          if (abortController.signal.aborted || disposed) break;
          setConnectionState('error');
        }

        if (disposed || abortController.signal.aborted) break;

        await waitForReconnect(backoffMs, abortController.signal);
        backoffMs = Math.min(backoffMs * 2, MAX_BACKOFF_MS);
      }
    };

    void run();

    return () => {
      disposed = true;
      abortController.abort();
    };
  }, [connect, enabled, workspaceId]);

  const displayedConnectionState: WorkspaceRealtimeConnectionState =
    !enabled || !workspaceId ? 'idle' : connectionState;

  return { connectionState: displayedConnectionState };
}
